import { useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { listPractices } from '../../../api/admin'
import { Button, ButtonLink } from '../../../components/ui/Button'
import { Card, ErrorBox } from '../../../components/ui/Card'
import { useFetch } from '../../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass, skeletonClass } from '@/lib/tableStyles'
import type { Practice } from '../../../types/admin'

const PAGE_SIZE = 25

export default function PracticeList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const offset = Number(searchParams.get('offset') ?? '0')
  const [inputValue, setInputValue] = useState(search)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, loading, error } = useFetch(
    () => listPractices({ search: search || undefined, limit: PAGE_SIZE, offset }),
    [search, offset],
  )

  const practices: Practice[] = data?.items ?? []
  const total = data?.total ?? 0

  const handleSearch = (v: string) => {
    setInputValue(v)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const p = new URLSearchParams()
      if (v) p.set('q', v)
      setSearchParams(p, { replace: true })
    }, 300)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + PAGE_SIZE, total)

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Practices</h1>
        <ButtonLink to="/admin/practices/new">+ New Practice</ButtonLink>
      </div>

      <div className="mb-4">
        <input
          className="py-[0.55rem] px-3 border border-border-input rounded text-sm outline-none bg-surface text-text w-[280px]"
          type="search"
          placeholder="Search by name…"
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Name</th>
                <th className={thClass}>Organisation</th>
                <th className={thClass}>City</th>
                <th className={thClass}>Phone</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Created</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className={`${skeletonClass} h-3.5 w-[70%]`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : practices.length === 0 ? (
                <tr>
                  <td colSpan={7} className={`${tdClass} text-center py-12 text-text-3`}>
                    {search ? 'No practices match your search.' : 'No practices yet.'}
                  </td>
                </tr>
              ) : (
                practices.map((p) => (
                  <tr key={p.id}>
                    <td className={`${tdClass} font-semibold`}>{p.name}</td>
                    <td className={`${tdClass} text-text-2`}>{p.organization_name ?? '—'}</td>
                    <td className={`${tdClass} text-text-2`}>{p.city ?? '—'}</td>
                    <td className={`${tdClass} text-text-2`}>{p.phone ?? '—'}</td>
                    <td className={`${tdClass} text-[0.82rem] ${p.is_active ? 'text-success-text' : 'text-danger-text'}`}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </td>
                    <td className={`${tdClass} text-text-2 text-[0.82rem]`}>
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <div className="flex gap-4 justify-end">
                        <Link to={`/practices/${p.id}/staff`} className="text-text-2 font-medium text-[0.82rem] no-underline">
                          Staff
                        </Link>
                        <Link to={`/admin/practices/${p.id}/edit`} className="text-accent font-medium text-[0.82rem] no-underline">
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-[0.85rem] border-t border-border text-[0.85rem] text-text-2">
            <span>Showing {start}–{end} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => { const np = new URLSearchParams(searchParams); np.set('offset', String(Math.max(0, offset - PAGE_SIZE))); setSearchParams(np, { replace: true }) }}>
                ← Prev
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => { const np = new URLSearchParams(searchParams); np.set('offset', String(offset + PAGE_SIZE)); setSearchParams(np, { replace: true }) }}>
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
